import React, { PureComponent } from 'react';
import { oneOfType, number, string, func } from 'prop-types';
import { connect } from 'react-redux';

import MessageIcon from 'mdi-react/MessageAlertIcon';
import ErrorIcon from 'mdi-react/AlertCircleOutlineIcon';
import CheckIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon';
import CloseIcon from 'mdi-react/CloseIcon';

import { colors } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import Spinner from '@prisjakt/ui-spinner';

import { ViewportContext } from '../../../viewport';
import Modal from '../../../modal';
import { reportWrongPrice } from '../../../../areas/feedback/actions';
import { postFeedback as feedbackRequest } from '../../../../globals/api/requests';

import * as S from './styles';

const WRONG_PRICE = 'price';
const WRONG_STOCK_STATUS = 'stock';
const WRONG_PRODUCT = 'wrong_product';

const closeIcon = <CloseIcon />;

class ReportButton extends PureComponent {
  state = {
    modalIsOpen: false,
    showButtons: false,
    sending: false,
    success: false,
    error: false,
  };

  static propTypes = {
    storeId: number.isRequired,
    priceId: oneOfType([string, number]).isRequired,
    priceValue: number.isRequired,
    stockStatus: string,
    reportWrongPrice: func.isRequired,
    onClose: func.isRequired,
  };

  static defaultProps = {
    stockStatus: 'unknown',
  };

  postFeedback = async data => {
    try {
      this.setState({ sending: true });

      const res = await feedbackRequest(data);

      if (res.errors) {
        this.setState({
          sending: false,
          error: true,
        });

        setTimeout(() => {
          this.props.onClose();
        }, 3000);

        throw new Error(
          `Error creating report on price: ${JSON.stringify(res.errors)}`
        );
      }

      if (res.data.feedback.success === true) {
        this.setState({
          sending: false,
          success: true,
        });

        setTimeout(() => {
          this.props.onClose();
        }, 3000);
      }
    } catch (e) {
      this.setState({
        sending: false,
        error: true,
      });

      setTimeout(() => {
        this.props.onClose();
      }, 3000);

      throw new Error(`Error creating report on price: ${JSON.stringify(e)}`);
    }
  };

  onReport = (e, type) => {
    const { storeId, priceId, priceValue, stockStatus } = this.props;

    if (!IS_BROWSER) return;

    // Stops click out action event
    e.preventDefault();
    e.stopPropagation();

    const message = `Error report on ${window.location.href}`;
    const metadata = {
      message,
      agent_type_key: type,
      uri: window.location.href,
      country_code: this.context.market.code,
      store_id: storeId,
      price_id: parseInt(priceId, 10),
      price_value: String(priceValue),
      stock_status: stockStatus,
    };

    this.props.reportWrongPrice(metadata); // Action event for tracking
    this.postFeedback(metadata);
  };

  openModal = e => {
    // Stops the PriceRow link and onClick from firing
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      modalIsOpen: true,
    });
  };

  closeModal = () => {
    this.setState({
      modalIsOpen: false,
    });
  };

  onShowButtons = e => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      showButtons: true,
    });
  };

  onClose = e => {
    e.preventDefault();
    e.stopPropagation();

    this.props.onClose();
  };

  reportWrongPrice = e => {
    this.onReport(e, WRONG_PRICE);
  };

  reportWrongStockStatus = e => {
    this.onReport(e, WRONG_STOCK_STATUS);
  };

  reportWrongProduct = e => {
    this.onReport(e, WRONG_PRODUCT);
  };

  render() {
    const { sending, success, error } = this.state;

    const completed = error || sending || success;

    return (
      <>
        <Modal isOpen={this.state.modalIsOpen} onRequestClose={this.closeModal}>
          <S.Title>
            <MessageIcon size={28} />
            <T domain="feedback">dialog.prompt-title</T>
          </S.Title>

          <S.ModalButton
            variant="outlined"
            color="primary"
            onClick={this.reportWrongPrice}
          >
            <T domain="feedback">report.wrong-price</T>
          </S.ModalButton>
          <S.ModalButton
            variant="outlined"
            color="primary"
            onClick={this.reportWrongStockStatus}
          >
            <T domain="feedback">report.wrong-stock-status</T>
          </S.ModalButton>

          <S.ModalButton
            variant="outlined"
            color="primary"
            onClick={this.reportWrongProduct}
          >
            <T domain="feedback">report.wrong-product</T>
          </S.ModalButton>

          {success || sending ? (
            <S.ModalFooter>
              {sending && <Spinner isActive position="none" size={30} />}

              {success && (
                <S.Message>
                  <CheckIcon color={colors.green1} />
                  <T domain="feedback">dialog.thanks-for-feedback</T>
                </S.Message>
              )}
            </S.ModalFooter>
          ) : null}
        </Modal>

        <S.ReportOverlay>
          {!completed && !this.state.showButtons && (
            <>
              <ViewportContext.Consumer>
                {({ isMobile }) => (
                  <>
                    <S.PromptTitle>
                      <T domain="feedback">dialog.prompt-title</T>
                    </S.PromptTitle>
                    <S.ToggleButtons
                      type="button"
                      onClick={isMobile ? this.openModal : this.onShowButtons}
                    >
                      <T domain="feedback">dialog.prompt-button</T>
                    </S.ToggleButtons>
                  </>
                )}
              </ViewportContext.Consumer>
            </>
          )}
          {!completed && this.state.showButtons && (
            <>
              <S.OverlayButton
                variant="outlined"
                size="small"
                color="primary"
                onClick={this.reportWrongPrice}
              >
                <T domain="feedback">report.wrong-price</T>
              </S.OverlayButton>
              <S.OverlayButton
                variant="outlined"
                size="small"
                color="primary"
                onClick={this.reportWrongStockStatus}
              >
                <T domain="feedback">report.wrong-stock-status</T>
              </S.OverlayButton>
              <S.OverlayButton
                variant="outlined"
                size="small"
                color="primary"
                onClick={this.reportWrongProduct}
              >
                <T domain="feedback">report.wrong-product</T>
              </S.OverlayButton>
            </>
          )}

          {sending && <Spinner isActive position="none" size={30} />}
          {success && (
            <S.Message>
              <CheckIcon color={colors.green1} />
              <T domain="feedback">dialog.thanks-for-feedback</T>
            </S.Message>
          )}
          {error && (
            <S.Message>
              <ErrorIcon color={colors.red1} />
              <T domain="feedback">dialog.request-error</T>
            </S.Message>
          )}

          <S.CloseButton
            variant="icon"
            icon={closeIcon}
            onClick={this.onClose}
          />
        </S.ReportOverlay>
      </>
    );
  }
}

ReportButton.contextType = AppContext;

export default connect(
  () => ({}),
  { reportWrongPrice }
)(ReportButton);
